import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, FormHelperText } from '@mui/material';
import DeleteIcon from "@mui/icons-material/Delete";
import { useLocation, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { groupStateList } from "../../common/StaticFieldList";
import NoDataFound from "../../common/NoDataFound";
import {
  TextField,
  IconButton,
  Checkbox,
  Box,
  Button,
  Table,
  TableBody,
  Stack,
  Select,
  MenuItem,
  Pagination,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Paper,
  useTheme,
  useMediaQuery,
  Typography,
  Tooltip,
} from '@mui/material';
import { styled } from "@mui/material/styles";
import { getCarrierAction,
  saveCarrierGroupAction,
  updateCarrierAction,
getAgencyCodeUserPswAction,
getCarrierListWithStateAction,
deleteCarrierGroupAction  } from '../../redux/actions/agencyActions';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: '#1565c0',
  color: theme.palette.common.white,
  fontFamily: '"Poppins", sans-serif',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  '& td, & th': {
    padding: theme.spacing(1.5),
    fontFamily: '"Poppins", sans-serif',
  },
}));

const CreateCarrier = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [companyGroupName, setGroupName] = useState('');
  const [selectedCheckbox, setSelectedCheckbox] = useState([]);
  const [formValues, setFormValues] = useState({ state: '' });
  const [formErrors, setFormErrors] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isEdit, setIsEdit] = useState(false);
  const [groupId, setGroupId] = useState(null);
  const [error, setError] = useState('');
  const [carrierInputValues, setCarrierInputValues] = useState({});
  const [editableRow, setEditableRow] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const get_company_data = useSelector((state) => state?.companies?.company_data || []);
  const state_carrier_list = useSelector((state) => state?.agency?.state_carrier_list || []);

  const carriers = state_carrier_list.content || [];
  const group_id = location?.state?.group?.id;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (location.state) {
      dispatch(getAgencyCodeUserPswAction(group_id));
    }
  }, [location.state]);

  useEffect(() => {
  if (location.state?.isEdit && get_company_data?.carriers?.length > 0) {
    const selectedCarrierIds = get_company_data.carriers.map(c => c.carrierId);

    const inputValues = {};
    get_company_data.carriers.forEach((carrier) => {
      inputValues[carrier.carrierId] = {
        agencyCode: carrier.agencyCode || "",
        agencyUsername: carrier.agencyUsername || "",
        agencyPassword: carrier.agencyPassword || "",
        producerCode: carrier.producerCode || carrier.producer_code || "",
      };
    });

    const firstCarrierStateCode = get_company_data.carriers[0]?.stateCode || "";

    setCarrierInputValues(inputValues);
    setSelectedCheckbox(selectedCarrierIds);
    setGroupName(get_company_data.name || "");
    setFormValues(prev => ({ ...prev, state: firstCarrierStateCode }));
    setIsEdit(true);
    setGroupId(get_company_data.id || null);

    if (firstCarrierStateCode) {
      dispatch(getCarrierListWithStateAction(firstCarrierStateCode));
    }
  }
}, [location.state, get_company_data, dispatch]);

  const handleFormFieldChange = (event) => {
    const { name, value } = event.target;

  setFormValues((prev) => {
    if (name === "state" && value !== prev.state) {
      dispatch(getCarrierListWithStateAction(value));
    }

    return {
      ...prev,
      [name]: value,
    };
  });

  setFormErrors((prev) => ({
    ...prev,
    [name]: "",
  }));
};

  const handleInputChange = (id, field, value) => {
    setCarrierInputValues((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
        stateCode: prev[id]?.stateCode || formValues.state,
      },
    }));

    setFieldErrors(prev => ({
      ...prev,
      [`${id}_${field}`]: ''
    }));
  };

  useEffect(() => {
    dispatch(getCarrierAction());
  }, [dispatch]);

  const handleCheckboxChange = (id) => {
    setSelectedCheckbox((prev) =>
      prev.includes(id)
        ? prev.filter((c) => c !== id)
        : [...prev, id]
    );

    setCarrierInputValues((prev) => {
      const newState = { ...prev };
      if (prev[id]) {
        delete newState[id];
      } else {
        const existing = (get_company_data?.carriers || []).find(c => c.carrierId === id);
          newState[id] = {
          agencyCode: existing?.agencyCode || "",
          agencyUsername: existing?.agencyUsername || "",
          agencyPassword: existing?.agencyPassword || "",
          producerCode: existing?.producerCode || existing?.producer_code || "",
          stateCode: formValues.state,
        };
      }
      return newState;
    });
  };

  const clearState = () => {
    setSelectedCheckbox([]);
    setGroupName('');
    setCarrierInputValues({});
    setIsEdit(false);
    setGroupId(null);
    setError('');
    setEditableRow(null);
    setFieldErrors({});
    dispatch({ type: 'CLEAR_CARRIER_LISTING_ITEM_SUCCESS' });
  };

  const handleCancel = () => {
    clearState();
    navigate('/CarrierGroup', { state: {} });
    dispatch({type:'CLEAR_CARRIER_LISTING_ITEM_SUCCESS'});
  };

 const validateFields = () => {
  let isValid = true;

  if (selectedCheckbox.length === 0) {
    setError('Please select at least one carrier.');
    isValid = false;
  } else {
    setError('');
  }

  return isValid;
};

const handleCreateOrUpdateGroupClick = () => {
  setError('');
  setFormErrors({});

  if (!validateFields()) {
    return;
  }

  const carriersData = selectedCheckbox.map((id) => ({
    carrierId: id,
    producerCode: carrierInputValues[id]?.producerCode ?? "",
    agencyCode: carrierInputValues[id]?.agencyCode ?? "",
    agencyUsername: carrierInputValues[id]?.agencyUsername ?? "",
    agencyPassword: carrierInputValues[id]?.agencyPassword ?? "",
    stateCode: formValues.state,
  }));

  const payload = {
    ...(isEdit && { id: groupId }),
    name: companyGroupName,
    isActive: true,
    stateCode: formValues.state,
    carriers: carriersData,
  };

  const action = isEdit ? updateCarrierAction : saveCarrierGroupAction;

  dispatch(action(payload))
    .then(() => {
      setSnackbar({
        open: true,
        message: `Carrier Group ${isEdit ? 'Updated' : 'Created'} successfully`,
        severity: 'success'
      });
      setTimeout(() => navigate('/CarrierGroup'), 1000);
      clearState();
    })
    .catch(error => {
      if (error.message && (
          error.message.includes("already exists") || 
          error.message.includes("duplicate") ||
          error.response?.status === 400
        )) {
        setSnackbar({
          open: true,
          message: "Group name already exists. Use different group name.",
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: error.message || `Failed to ${isEdit ? 'update' : 'create'} carrier group`,
          severity: 'error'
        });
      }
    });
};

  const handleRowSave = (carrierId) => {
    const carrierData = carrierInputValues[carrierId];
    

    if (!carrierData) {
      setSnackbar({
        open: true,
        message: 'Carrier data not found. Please try again.',
        severity: 'error',
      });
      return;
    }

    const previousCarrier = get_company_data?.carriers?.find(c => c.carrierId === carrierId);
    const hasChanged =
      !previousCarrier ||
      carrierData.producerCode !== previousCarrier.producerCode ||
      carrierData.agencyCode !== previousCarrier.agencyCode ||
      carrierData.agencyUsername !== previousCarrier.agencyUsername ||
      carrierData.agencyPassword !== previousCarrier.agencyPassword;

    if (!hasChanged) {
      setSnackbar({
        open: true,
        message: 'No changes to save.',
        severity: 'info',
      });
      setEditableRow(null);
      return;
    }

  const payload = {
    id: get_company_data.id,
    name: companyGroupName,
    isActive: true,
    stateCode: formValues.state,
    carriers: [
      {
        carrierId,
        producerCode: carrierData.producerCode ?? "",
        agencyCode: carrierData.agencyCode ?? "",
        agencyUsername: carrierData.agencyUsername ?? "",
        agencyPassword: carrierData.agencyPassword ?? "",
        stateCode: formValues.state,
      },
    ],
  };

    dispatch(updateCarrierAction(payload))
      .then(() => {
        dispatch(getCarrierAction());
        setSnackbar({
          open: true,
          message: 'Carrier updated successfully.',
          severity: 'success',
        });
        setEditableRow(null);
      })
      .catch(error => {
        setSnackbar({
          open: true,
          message: error.message || 'Failed to update carrier',
          severity: 'error'
        });
      });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteCarrier = async (carrierId, stateCode) => {
    try {
      const updatedSelected = selectedCheckbox.filter(id => id !== carrierId);
      setSelectedCheckbox(updatedSelected);
      await dispatch(deleteCarrierGroupAction(groupId, carrierId, stateCode));
      await dispatch(getAgencyCodeUserPswAction(groupId));
      await dispatch(getCarrierListWithStateAction(stateCode));

      setSnackbar({ open: true, message: 'Carrier permanently deleted', severity: 'success' });
    } catch (error) {
      setSelectedCheckbox(prev => [...prev, carrierId]);
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    }
  };
  const getTruncatedText = (text, maxLength = 15) => {
    if (!text) return "N/A";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const paginatedRows = carriers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{
      padding: isMobile ? 2 : 3,
      margin: isMobile ? '10px' : '20px',
      border: '1px solid rgba(57, 57, 57, 0.1)',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      backgroundColor: '#ffffff',
      minHeight: '100vh'
    }} id="carriergroupscreen">


        <Typography
          id="Create Carrier Group"
          variant={isMobile ? "h6" : "h5"}
          sx={{
            color: theme.palette.primary.main,
            marginBottom: isMobile ? 2 : 3,
            fontSize: isMobile ? '18px' : '24px',
            fontWeight: 'bold'
          }}
        >
          {isEdit ? "Update Carrier Group" : "Create Carrier Group"}
        </Typography>
       <Box mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
        <Box sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          padding: "10px 0",
          gap: isMobile ? 2 : 0,
          marginBottom: isMobile ? 2 : 3
        }}>

          <Box sx={{
            display: "flex",
            flexFlow: 'column',
            gap: 0,
            flex: isMobile ? '1' : '0 0 auto'
          }}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Poppins",
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: "500",
                lineHeight: "19.6px",
                textAlign: "left",
                marginBottom: "4px",
              }}
            >
              Carrier Group Name
            </Typography>
            <TextField
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              value={companyGroupName}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^(?!.* {2,})[A-Za-z0-9&\-_ ]*$/.test(value)) {
                  setGroupName(value);
                  setError('');
                } else {
                  setError('Only alphanumeric, single space, &, hyphen, underscore allowed');
                }
              }}
              error={!!error}
              helperText={error}
              sx={{
                width: isMobile ? "100%" : "220px",
                height: isMobile ? "45px" : "55px",
                "& .MuiInputBase-root": {
                  height: isMobile ? "45px" : "55px",
                  fontSize: isMobile ? "12px" : "14px"
                },
              }}
            />
          </Box>

          <Box sx={{
            display: "flex",
            flexFlow:'column',
            gap: 0,
            margin: isMobile ? 0 : '0 20px',
            flex: isMobile ? '1' : '0 0 auto'
          }}>
            <Typography variant="body2" component="label" htmlFor="state"
            sx={{
              fontFamily: "Poppins",
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "500",
              lineHeight: "19.6px",
              textAlign: "left",
              marginBottom: "4px",
            }}>
              State <span style={{ color: "red" }}>*</span>
            </Typography>

            <FormControl fullWidth error={!!formErrors.state}>
              <Select
                native
                value={formValues.state}
                onChange={handleFormFieldChange}
                size={isMobile ? "small" : "medium"}
                inputProps={{
                  name: "state",
                  id: "state-select",
                }}
                sx={{
                  width: isMobile ? "100%" : "200px",
                  fontFamily: 'Poppins',
                  backgroundColor: "#ffffff",
                  borderRadius: "4px",
                  fontSize: isMobile ? "12px" : "14px",
                  '& .MuiSelect-select': {
                    border: formErrors.state ? "1px solid #d32f2f" : "1px solid #ccc",
                    padding: isMobile ? "8px" : "10px",
                  },
                  '&:focus': {
                    '& .MuiSelect-select': {
                      borderColor: formErrors.state ? '#d32f2f' : 'black',
                    }
                  },
                  '&:hover': {
                    '& .MuiSelect-select': {
                      borderColor: formErrors.state ? '#d32f2f' : '#999',
                    }
                  },
                }}
              >
                <option value="" disabled>Select a state</option>
                {groupStateList.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </Select>
              {formErrors.state && <FormHelperText>{formErrors.state}</FormHelperText>}
            </FormControl>
          </Box>

          <Box sx={{
            marginTop: isMobile ? "16px" : "2px",
            display:'flex',
            justifyContent: isMobile ? 'stretch' : 'end',
            alignItems: isMobile ? 'stretch' : 'end',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0,
            flex: isMobile ? '1' : '0 0 auto'
          }}>
            <Button
              variant="outlined"
              onClick={handleCreateOrUpdateGroupClick}
            >
              {isEdit ? "Update Group" : "Create Group"}
            </Button>
            <Button
              variant="outlined"
              sx={{ml:2}}
              onClick={handleCancel}
             
            >
              Cancel
            </Button>
          </Box>
        </Box>
        </Box>

        {formValues.state ? (
        <TableContainer
          component={Paper}
          sx={{
            marginTop:'0px',
            position: 'relative',
            minHeight: '200px',
            maxHeight: isMobile ? '60vh' : 'none',
            overflow: 'auto',
            boxShadow: isMobile ? 1 : 2
          }}
        >
          <Table
            id="carriergrouptable"
            stickyHeader
            sx={{
              minWidth: isMobile ? '800px' : '900px',
              '& .MuiTableCell-root': {
                fontSize: isMobile ? '10px' : '14px',
                padding: isMobile ? '4px 6px' : '8px 16px',
                whiteSpace: isMobile ? 'nowrap' : 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: isMobile ? '100px' : '150px'
              }
            }}
            size={isMobile ? "small" : "medium"}
          >
            <TableHead>
              <TableRow>
                <StyledTableCell
                  className="styled-table-heading"
                  sx={{
                    position: 'sticky',
                    top: 0,
                    fontSize: isMobile ? '10px' : '14px',
                    padding: isMobile ? '6px' : '16px',
                    minWidth: isMobile ? '50px' : '70px'
                  }}
                >
                  Active
                </StyledTableCell>
                <StyledTableCell
                  className="styled-table-heading"
                  sx={{
                    position: 'sticky',
                    top: 0,
                    fontSize: isMobile ? '10px' : '14px',
                    padding: isMobile ? '6px' : '16px',
                    minWidth: isMobile ? '100px' : '150px'
                  }}
                >
                  Company Name
                </StyledTableCell>
                <StyledTableCell
                  className="styled-table-heading"
                  sx={{
                    position: 'sticky',
                    top: 0,
                    fontSize: isMobile ? '10px' : '14px',
                    padding: isMobile ? '6px' : '16px',
                    minWidth: isMobile ? '80px' : '100px'
                  }}
                >
                  Status
                </StyledTableCell>
                <StyledTableCell
                  className="styled-table-heading"
                  sx={{
                    position: 'sticky',
                    top: 0,
                    fontSize: isMobile ? '10px' : '14px',
                    padding: isMobile ? '6px' : '16px',
                    minWidth: isMobile ? '80px' : '100px'
                  }}
                >
                  Producer Code
                </StyledTableCell>
                <StyledTableCell
                  className="styled-table-heading"
                  sx={{
                    position: 'sticky',
                    top: 0,
                    fontSize: isMobile ? '10px' : '14px',
                    padding: isMobile ? '6px' : '16px',
                    minWidth: isMobile ? '90px' : '120px'
                  }}
                >
                  Agency Code
                </StyledTableCell>
                <StyledTableCell
                  className="styled-table-heading"
                  sx={{
                    position: 'sticky',
                    top: 0,
                    fontSize: isMobile ? '10px' : '14px',
                    padding: isMobile ? '6px' : '16px',
                    minWidth: isMobile ? '100px' : '130px'
                  }}
                >
                  Agency Username
                </StyledTableCell>
                <StyledTableCell
                  className="styled-table-heading"
                  sx={{
                    position: 'sticky',
                    top: 0,
                    fontSize: isMobile ? '10px' : '14px',
                    padding: isMobile ? '6px' : '16px',
                    minWidth: isMobile ? '100px' : '130px'
                  }}
                >
                  Agency Password
                </StyledTableCell>
                {isEdit && (
                  <StyledTableCell
                    className="styled-table-heading"
                    sx={{
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '10px' : '14px',
                      padding: isMobile ? '6px' : '16px',
                      minWidth: isMobile ? '80px' : '100px'
                    }}
                  >
                    Action
                  </StyledTableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((group, index) => {
                const carrierId = group.id;
                const carrierData = carrierInputValues[carrierId];

                return (
                  <StyledTableRow key={index} className="styled-table-row" id={`carriergrouprow${index}`}>
                    <TableCell>
                      <Checkbox
                        checked={selectedCheckbox.includes(carrierId)}
                        onChange={() => handleCheckboxChange(carrierId)}
                        id={`active-${index}`}
                        size={isMobile ? "small" : "medium"}
                      />
                    </TableCell>
                    <TableCell>
                      {isMobile ? (
                        <Tooltip title={group.insuranceCarrier || "N/A"} arrow placement="top">
                          <span>{getTruncatedText(group.insuranceCarrier, 12)}</span>
                        </Tooltip>
                      ) : (
                        group.insuranceCarrier
                      )}
                    </TableCell>
                    <TableCell>
                      {isMobile ? (
                        <Tooltip title={group.programName || "N/A"} arrow placement="top">
                          <span>{getTruncatedText(group.programName, 10)}</span>
                        </Tooltip>
                      ) : (
                        group.programName
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={carrierInputValues[carrierId]?.producerCode || ""}
                        onChange={(e) => handleInputChange(carrierId, "producerCode", e.target.value)}
                        disabled={isEdit && editableRow !== carrierId}
                        error={!!fieldErrors[`${carrierId}_producerCode`]}
                        helperText={fieldErrors[`${carrierId}_producerCode`]}
                      />
                    </TableCell>

                    <TableCell>
                      <TextField
                        size="small"
                        value={carrierData?.agencyCode || ''}
                        onChange={(e) =>
                          handleInputChange(carrierId, 'agencyCode', e.target.value)
                        }
                        disabled={isEdit && editableRow !== carrierId}
                        error={!!fieldErrors[`${carrierId}_agencyCode`]}
                        helperText={fieldErrors[`${carrierId}_agencyCode`]}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: isMobile ? '10px' : '14px',
                            height: isMobile ? '28px' : '40px',
                          },
                          '& .MuiInputBase-input': {
                            padding: isMobile ? '4px 6px' : '8px 12px',
                          },
                          width: isMobile ? '70px' : '100px'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={carrierData?.agencyUsername || ''}
                        onChange={(e) =>
                          handleInputChange(carrierId, 'agencyUsername', e.target.value)
                        }
                        disabled={isEdit && editableRow !== carrierId}
                        error={!!fieldErrors[`${carrierId}_agencyUsername`]}
                        helperText={fieldErrors[`${carrierId}_agencyUsername`]}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: isMobile ? '10px' : '14px',
                            height: isMobile ? '28px' : '40px',
                          },
                          '& .MuiInputBase-input': {
                            padding: isMobile ? '4px 6px' : '8px 12px',
                          },
                          width: isMobile ? '80px' : '120px'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="password"
                        value={carrierData?.agencyPassword || ''}
                        onChange={(e) =>
                          handleInputChange(carrierId, 'agencyPassword', e.target.value)
                        }
                        disabled={isEdit && editableRow !== carrierId}
                        error={!!fieldErrors[`${carrierId}_agencyPassword`]}
                        helperText={fieldErrors[`${carrierId}_agencyPassword`]}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: isMobile ? '10px' : '14px',
                            height: isMobile ? '28px' : '40px',
                          },
                          '& .MuiInputBase-input': {
                            padding: isMobile ? '4px 6px' : '8px 12px',
                          },
                          width: isMobile ? '80px' : '120px'
                        }}
                      />
                    </TableCell>

                    {isEdit && (
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-start' }}>
                          {editableRow === carrierId ? (
                            <IconButton
                              size={isMobile ? "small" : "medium"}
                              sx={{ p: isMobile ? '2px' : '4px' }}
                              onClick={() => handleRowSave(carrierId)}
                            >
                              <SaveIcon fontSize={isMobile ? "small" : "medium"} />
                            </IconButton>
                          ) : (
                            <IconButton
                              size={isMobile ? "small" : "medium"}
                              sx={{ p: isMobile ? '2px' : '4px' }}
                              onClick={() => {
                                if (!formValues.state) {
                                  setSnackbar({
                                    open: true,
                                    message: 'Please select a state before editing.',
                                    severity: 'error',
                                  });
                                  return;
                                }
                                setEditableRow(carrierId);
                              }}
                            >
                              <EditIcon fontSize={isMobile ? "small" : "medium"} />
                            </IconButton>
                          )}
                          {selectedCheckbox.includes(carrierId) && (
                            <IconButton
                              size={isMobile ? "small" : "medium"}
                              sx={{ p: isMobile ? '2px' : '4px' }}
                              onClick={() => {
                                const matchedCarrier = get_company_data?.carriers?.find(c => c.carrierId === carrierId);
                                const stateCode = matchedCarrier?.stateCode || formValues.state;
                                handleDeleteCarrier(carrierId, stateCode);
                              }}
                            >
                              <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : !isEdit ? (
           <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                    }}>
          <NoDataFound
            discription={"Please enter Group Name and select State to create group"}
            message={"After select State select Company Name to get started."}
          />
          </Box>
        ): null}

        {formValues.state && carriers.length > 0 && (
          <Box sx={{
            width: "100%",
            overflow: "hidden",
            padding: isMobile ? "12px 0" : "16px 0"
          }}>
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="flex-end"
              alignItems="center"
              flexWrap="wrap"
              spacing={2}
              sx={{
                gap: isMobile ? 1 : 2
              }}
            >
              {/* Rows per page dropdown */}
              <Box display="flex" alignItems="center">
                <Typography variant="body2" sx={{
                  fontSize: isMobile ? "11px" : "14px",
                  marginRight: "8px"
                }}>
                  Rows per page:
                </Typography>
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    height: isMobile ? '32px' : '36px',
                    fontSize: isMobile ? '11px' : '14px',
                    minWidth: isMobile ? '60px' : '80px',
                    '& .MuiSelect-select': {
                      padding: isMobile ? '8px 32px 8px 12px' : '10px 32px 10px 12px'
                    }
                  }}
                >
                  {[5, 10, 15, 25, 50].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Pagination
                count={Math.ceil(carriers.length / rowsPerPage)}
                page={page + 1}
                onChange={(event, value) => handleChangePage(event, value - 1)}
                showFirstButton
                showLastButton
                size={isMobile ? "small" : "medium"}
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: isMobile ? '12px' : '14px',
                    minWidth: isMobile ? '28px' : '32px',
                    height: isMobile ? '28px' : '32px'
                  }
                }}
              />
            </Stack>
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: isMobile ? 'center' : 'right'
          }}
          sx={{
            top: isMobile ? '60px' : '24px',
            '& .MuiSnackbarContent-root': {
              fontSize: isMobile ? '12px' : '14px'
            }
          }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{
              whiteSpace: 'pre-line',
              fontSize: isMobile ? '12px' : '14px',
              width: isMobile ? '90vw' : 'auto',
              maxWidth: isMobile ? '350px' : '500px'
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </Box>
  );
};

export default CreateCarrier;